import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  FileText, 
  FolderOpen, 
  GitBranch, 
  Clock, 
  GitPullRequest, 
  GitFork, 
  Flask, 
  Plus, 
  Users, 
  Edit,
  GitMerge,
  FlaskRoundIcon
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getRecentActivity } from '../../../services/api';

const RecentActivityBlock = ({ user, size = 'medium', refreshTrigger }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    loadActivities();
  }, [user, refreshTrigger]);

  const loadActivities = async () => {
    if (!user?.organization_id) {
      setLoading(false);
      return;
    }
    
    try {
      const activityData = await getRecentActivity(user.organization_id, {
        limit: getDisplayCount() * 2, // Get more to ensure we have enough after filtering
        days: 7
      });
      
      // Ensure activityData is an array
      const activities = Array.isArray(activityData) ? activityData : [];
      
      // Transform activity data with proper icons and colors
      const transformedActivities = activities.map(activity => {
        const { icon, color } = getActivityIconAndColor(activity.activity_type);
        
        // Parse timestamp correctly and handle potential future timestamps
        let timestamp = new Date();
        if (activity.created_at) {
          try {
            timestamp = new Date(activity.created_at);
            
            // Validate the parsed timestamp and handle future timestamps
            if (isNaN(timestamp.getTime())) {
              console.warn('Invalid timestamp:', activity.created_at);
              timestamp = new Date();
            } else if (timestamp > new Date()) {
              // If timestamp is in the future, clamp it to current time
              // This handles cases where database has incorrect future timestamps
              console.warn('Future timestamp detected, using current time:', activity.created_at);
              timestamp = new Date();
            }
          } catch (error) {
            console.warn('Failed to parse timestamp:', activity.created_at, error);
            timestamp = new Date();
          }
        }
        
        return {
          id: activity.id,
          type: activity.activity_type,
          title: activity.title,
          timestamp,
          icon,
          color,
          user_name: activity.user_name,
          prompt_name: activity.prompt_name,
          project_name: activity.project_name,
          score: activity.score
        };
      });
      
      setActivities(transformedActivities);
    } catch (error) {
      console.error('Failed to load activities:', error);
      setActivities([]); // Ensure activities is set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const getActivityIconAndColor = (activityType) => {
    switch (activityType) {
      case 'prompt_executed':
        return { icon: FileText, color: 'blue' };
      case 'branch_created':
        return { icon: GitBranch, color: 'green' };
      case 'pull_request_created':
        return { icon: GitPullRequest, color: 'purple' };
      case 'pull_request_merged':
        return { icon: GitMerge, color: 'green' };
      case 'fork_created':
        return { icon: GitFork, color: 'orange' };
      case 'experiment_created':
        return { icon: FlaskRoundIcon, color: 'indigo' };
      case 'prompt_created':
        return { icon: Plus, color: 'blue' };
      case 'project_created':
        return { icon: FolderOpen, color: 'green' };
      case 'version_created':
        return { icon: Edit, color: 'blue' };
      case 'collaboration_invite':
        return { icon: Users, color: 'purple' };
      default:
        return { icon: Activity, color: 'gray' };
    }
  };

  const getDisplayCount = () => {
    switch (size) {
      case 'small':
        return 4;
      case 'medium':
        return 8;
      default:
        return 4;
    }
  };

  const displayActivities = activities.slice(0, getDisplayCount());

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-celadon-100 text-celadon-600 dark:bg-celadon-900/30 dark:text-celadon-400',
      green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
      gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400',
      red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
    };
    return colorMap[color] || colorMap.blue;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  console.log(activities);
  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <Activity className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
        <p className="text-sm text-gray-600 dark:text-gray-400">No recent activity</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Activity</span>
      </div>

      <div className="space-y-3 overflow-y-auto max-h-full custom-scrollbar">
        {displayActivities.map((activity) => {
          const IconComponent = activity.icon;
          
          return (
            <div key={`${activity.type}-${activity.id}`} className="flex items-start gap-3 p-3 bg-gray-50/80 hover:bg-gray-100/80 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 rounded-2xl transition-all duration-200 border border-gray-100/50 hover:border-gray-200/80 dark:border-gray-600/50 dark:hover:border-gray-500/80 hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.3)] group">
              <div className={`p-2 rounded-xl flex-shrink-0 ${getColorClasses(activity.color)} group-hover:scale-105 transition-transform duration-200`}>
                <IconComponent className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-gray-100 line-clamp-2 font-medium mb-1">
                  {activity.title}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <Clock className="h-3 w-3" />
                  <span>
                    {activity.timestamp && !isNaN(activity.timestamp.getTime()) 
                      ? formatDistanceToNow(activity.timestamp, { addSuffix: true })
                      : 'Just now'
                    }
                  </span>
                  {activity.user_name && (
                    <>
                      <span>•</span>
                      <span>{activity.user_name}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default RecentActivityBlock; 